import {
  IPayerOverrideRow,
  IPayerOverrideSaveRequest,
} from "types/BenefitSelection/BenefitSelection";

import { api } from ".";

/**
 * CLE-2792 — `BenefitPayerProfile`: what a payer's plans really charge, where the response is wrong
 * or silent.
 *
 * SuperAdmin on the reads as well as the writes, because an override is a statement about a payer
 * that applies to every account that payer serves — adding one hands a dollar figure to estimates
 * that carry none today.
 *
 * 🚨 THE WIRE UNIT IS `kind`, NEVER `mode`. The API derives `mode` and `afterSource` from it; no
 * response carries `mode` and no request may send one. The three kinds are the three sentences the
 * screen shows — see `payerOverrideWording.ts`.
 *
 * 🚨 PLAN NAMES TRAVEL AS `planNames`, AN ARRAY, AND NEVER AS A JOINED STRING. A plan name may
 * contain a comma — `BASIC SECONDARY,PRIMARY DISABILITY,BASIC` is ONE plan — and the API 400s a
 * non-array rather than splitting it. The single `planName` field it replaced is gone from the
 * contract; the column is dropped a release later.
 */
const benefitPayerProfileQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Every override, newest first. With `accountId`, the ones that could govern that account —
     * which INCLUDES the rows naming no account, since those govern it too.
     */
    getPayerOverrides: builder.query<
      { data: IPayerOverrideRow[]; message: string },
      { accountId?: string } | void
    >({
      query: (args) =>
        args?.accountId
          ? `benefitPayerProfile?accountId=${encodeURIComponent(
              args.accountId
            )}`
          : "benefitPayerProfile",
      providesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /**
     * A body with no scope field is an override that applies to every payer and every account;
     * leaving the fields empty is how that is said (Mike, 2026-09-14). No confirmation flag.
     */
    createPayerOverride: builder.mutation<
      { data: IPayerOverrideRow; message: string },
      IPayerOverrideSaveRequest
    >({
      query: (body) => ({
        url: "benefitPayerProfile",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /** The whole row is written, so the body must carry every field, scope included. */
    updatePayerOverride: builder.mutation<
      { data: IPayerOverrideRow; message: string },
      { id: string; body: IPayerOverrideSaveRequest }
    >({
      query: ({ id, body }) => ({
        url: `benefitPayerProfile/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
    /** 🚨 A HARD DELETE — the table has no versioning. The API writes the row to `EventLog` first. */
    deletePayerOverride: builder.mutation<
      { data: { id: string }; message: string },
      string
    >({
      query: (id: string) => ({
        url: `benefitPayerProfile/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "BenefitPayerProfile" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetPayerOverridesQuery,
  useCreatePayerOverrideMutation,
  useUpdatePayerOverrideMutation,
  useDeletePayerOverrideMutation,
} = benefitPayerProfileQueries;
